import { accessTokenStateAtRender, computed, useCookie, useDayjs, useNuxtApp, usePlayerAccountClient, useSessionExpiry, createSharedComposable, ACCOUNT_RESTRICTION_STATUSES } from '#imports'
import type { UseQueryOptions } from '../../types'
import { isPrefetching } from '../../utils/prefetch'

declare module '#app' {
    interface NuxtApp {
        /** The render's one user-info instance; the server creates the app per request. */
        _userInfo?: ReturnType<typeof _useUserInfo>
    }
}

const _useUserInfo = (options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}, assumeLoggedIn = false) => {
    const dayjs = useDayjs()
    const { useGetUserInfo } = usePlayerAccountClient()

    const _loggedInBefore = useCookie<number | string | undefined>('loggedInBefore')
    const userJustLoggedIn = useCookie<true | undefined>('userJustLoggedIn', { secure: true })
    const userJustSignedUp = useCookie<true | undefined>('userJustSignedUp', { secure: true })
    const returnUrlAfterLogin = useCookie<string | undefined>('returnUrlAfterLogin', { secure: true, maxAge: dayjs.duration({ minutes: 30 }).asSeconds() })

    const query = useGetUserInfo(options)

    const isLoggedIn = computed(() => !!query.data.value || assumeLoggedIn)
    const isStaff = computed(() => query.data.value?.isStaff ?? false)
    /**
     * Is the user an existing user that has logged in before.
     *
     * It is determined by the `loggedInBefore` cookie and returns `true` if the cookie has any value.
     */
    const isExisting = computed(() => !!_loggedInBefore.value || isLoggedIn.value)

    const playerState = computed(() => {
        if (isLoggedIn.value) {
            return 'logged_in'
        }

        if (isExisting.value) {
            return 'existing'
        }

        return 'new'
    })

    const isAccountRestricted = computed(() => ACCOUNT_RESTRICTION_STATUSES.has(query.data.value?.checkResult?.type ?? ''))
    const canUsePlayerFeatures = computed(() => isLoggedIn.value && !isAccountRestricted.value)

    return query.enrich({
        userJustLoggedIn,
        userJustSignedUp,
        returnUrlAfterLogin,
        isLoggedIn,
        isStaff,
        isExisting,
        playerState,
        isAccountRestricted,
        canUsePlayerFeatures,
    })
}

/** One instance per render: its consumers share one `user/info` call and its result, a 401 included. */
function useUserInfoPerRequest(options?: Parameters<typeof _useUserInfo>[0]): ReturnType<typeof _useUserInfo> {
    const nuxtApp = useNuxtApp()

    if (isPrefetching(nuxtApp)) {
        // Declarations can run before user/info answers: a session with a fresh token is logged in.
        const tokenLoggedIn = !!useSessionExpiry().value && accessTokenStateAtRender() === 'fresh'

        return _useUserInfo(options, tokenLoggedIn)
    }

    return nuxtApp._userInfo ??= _useUserInfo({ server: true, lazy: false })
}

/**
 * Shared on the client so all call sites (3 per mounted GameTile) reuse one instance
 * instead of each creating 5 `useCookie` refs — every ref opens a BroadcastChannel
 * and posts a message on instantiation, which made tile-heavy pages O(N²) in mounted
 * tiles. The underlying query is already deduped by its `playerAccount:userInfo` key.
 *
 * `options` from the first caller win on the client. On the server they count only inside a
 * prefetch: elsewhere every caller gets the render's instance, with `server: true, lazy: false`.
 */
export const useUserInfo = import.meta.client ? createSharedComposable(_useUserInfo) : useUserInfoPerRequest
